// Secure Access — OLIVIUM access codes (operator, admin-gated surface).
//
// Wired to /api/square/command-center/access (GET) plus the
// access-codes create / revoke / expire POST routes. Every route is
// server-side, admin-gated (minLevel 80), and fails closed. Access
// codes are hash-at-rest: the one-time plaintext access_code is
// returned exactly once by accessCodes.create and NEVER again — no
// route ever returns code_hash or plaintext in a list.
//
// Secret-safety contract enforced here:
//   • The grid shows code_prefix only — never code_hash, never plaintext.
//   • The one-time plaintext is revealed exactly once, in a dedicated
//     reveal modal, right after create, behind a "copy now — shown once,
//     never again" warning. It is dropped from state on modal close and
//     is never logged.
//
// Non-happy states handled explicitly: loading, auth-required (sign in),
// forbidden (operator access required), and any other error (honest
// warning banner).

import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  DataGrid,
  Banner,
  InlineAlert,
  StatusBadge,
  Modal,
  ConfirmDialog,
  FormField,
  Input,
  Select,
} from '@square/ui'
import {
  api,
  ApiError,
  isAuthError,
  isForbiddenError,
  type CommandCenterAccessCode,
} from '../../../api/client'
import EmptyState from '../../../components/EmptyState'
import RouteHeader from '../../RouteHeader'

// ----------------------------------------------------------------
// Form option vocabularies — mirror the accessCodes.create payload
// union types in api/client.ts exactly. NEVER invent a value the
// server does not accept.
// ----------------------------------------------------------------

type Audience = 'operator' | 'team' | 'client'
type Role = 'operator' | 'client_owner' | 'client_collaborator' | 'viewer'
type ExpiresIn = '1h' | '24h' | '7d' | '30d' | 'never'

const AUDIENCE_OPTIONS: { value: Audience; label: string }[] = [
  { value: 'operator', label: 'Operator' },
  { value: 'team', label: 'Team' },
  { value: 'client', label: 'Client' },
]

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: 'operator', label: 'Operator' },
  { value: 'client_owner', label: 'Client owner' },
  { value: 'client_collaborator', label: 'Client collaborator' },
  { value: 'viewer', label: 'Viewer' },
]

const EXPIRES_OPTIONS: { value: ExpiresIn; label: string }[] = [
  { value: '1h', label: '1 hour' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'never', label: 'Never' },
]

// Secure Access target binding — a code SHOULD open a real destination.
// '' means "no destination (legacy/operator code)". Every other value
// mirrors the CHECK vocabulary in the target-binding migration and the
// accessTargetDestination() resolver on the server.
type TargetType =
  | '' | 'client_portal' | 'intake_invite' | 'document_workspace' | 'document_artifact'
  | 'spreadsheet_artifact' | 'project_workspace' | 'secure_portal'

const TARGET_OPTIONS: { value: TargetType; label: string }[] = [
  { value: '', label: 'No destination (unbound code)' },
  { value: 'client_portal', label: 'Client portal' },
  { value: 'intake_invite', label: 'Intake invite (receiver link)' },
  { value: 'document_workspace', label: 'Document workspace' },
  { value: 'document_artifact', label: 'Document artifact (Vault)' },
  { value: 'spreadsheet_artifact', label: 'Spreadsheet artifact' },
  { value: 'project_workspace', label: 'Project / workspace' },
  { value: 'secure_portal', label: 'Secure portal' },
]

// Map the free-form code.status string onto canonical StatusBadge
// tones. Unknown statuses fall through to a neutral tone rather than
// inventing a meaning.
function statusTone(status: string): 'live' | 'pending' | 'offline' | 'critical' | 'verified' | 'info' {
  const s = (status || '').toLowerCase()
  if (s === 'active') return 'live'
  if (s === 'claimed') return 'verified'
  if (s === 'revoked') return 'critical'
  if (s === 'expired') return 'offline'
  if (s === 'pending') return 'pending'
  return 'info'
}

function formatStamp(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd} ${hh}:${mi}`
}

const monoCell: React.CSSProperties = {
  display: 'inline-block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  fontFamily: 'var(--font-mono)',
}

interface AccessSummary {
  active: number
  claimed: number
  revoked: number
  expired: number
  total: number
}

const EMPTY_SUMMARY: AccessSummary = { active: 0, claimed: 0, revoked: 0, expired: 0, total: 0 }

const REVOKE_REASON = 'operator revoke'

export default function SecureAccess() {
  const [codes, setCodes] = useState<CommandCenterAccessCode[]>([])
  const [summary, setSummary] = useState<AccessSummary>(EMPTY_SUMMARY)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)
  const [forbidden, setForbidden] = useState(false)
  const [busy, setBusy] = useState(false)

  // Issue-code form modal state.
  const [issueOpen, setIssueOpen] = useState(false)
  const [label, setLabel] = useState('')
  const [audience, setAudience] = useState<Audience>('operator')
  const [role, setRole] = useState<Role>('operator')
  const [maxUses, setMaxUses] = useState('1')
  const [expiresIn, setExpiresIn] = useState<ExpiresIn>('24h')
  const [projectId, setProjectId] = useState('')
  const [targetType, setTargetType] = useState<TargetType>('')
  const [targetId, setTargetId] = useState('')
  const [targetLabel, setTargetLabel] = useState('')

  // Redeem panel state — operator can verify a code opens its destination.
  const [redeemInput, setRedeemInput] = useState('')
  const [redeeming, setRedeeming] = useState(false)
  const [redeemResult, setRedeemResult] = useState<
    | { ok: true; message: string; destination: string | null; targetLabel: string | null; targetType: string | null }
    | { ok: false; message: string }
    | null
  >(null)

  // One-time reveal modal state. `revealCode` holds the plaintext for
  // exactly one modal lifetime. It is dropped on close and never logged.
  const [revealCode, setRevealCode] = useState<string | null>(null)
  const [revealPrefix, setRevealPrefix] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Row-action targets.
  const [revokeTarget, setRevokeTarget] = useState<CommandCenterAccessCode | null>(null)
  const [expireTarget, setExpireTarget] = useState<CommandCenterAccessCode | null>(null)

  function formatError(err: unknown): string {
    if (err instanceof ApiError) {
      if (!err.message || err.message === err.code) return err.code
      return `${err.code}: ${err.message}`
    }
    if (err instanceof Error) return err.message || 'request_failed'
    return String(err)
  }

  // Route any thrown error into the correct honest state. Returns true
  // when it was an auth/forbidden case the caller should stop on.
  const routeError = useCallback((err: unknown): boolean => {
    if (isAuthError(err)) {
      setAuthRequired(true)
      setForbidden(false)
      setError(null)
      setCodes([])
      setSummary(EMPTY_SUMMARY)
      return true
    }
    if (isForbiddenError(err) || (err instanceof ApiError && err.code === 'forbidden')) {
      setForbidden(true)
      setAuthRequired(false)
      setError(null)
      setCodes([])
      setSummary(EMPTY_SUMMARY)
      return true
    }
    setError(formatError(err))
    return false
  }, [])

  const fetchCodes = useCallback(async () => {
    setLoading(true)
    setError(null)
    setAuthRequired(false)
    setForbidden(false)
    try {
      const result = await api.commandCenter.access()
      setCodes(result.codes || [])
      setSummary(result.summary || EMPTY_SUMMARY)
    } catch (err) {
      routeError(err)
    } finally {
      setLoading(false)
    }
  }, [routeError])

  useEffect(() => { void fetchCodes() }, [fetchCodes])

  function resetIssueForm() {
    setLabel('')
    setAudience('operator')
    setRole('operator')
    setMaxUses('1')
    setExpiresIn('24h')
    setProjectId('')
    setTargetType('')
    setTargetId('')
    setTargetLabel('')
  }

  async function onRedeem() {
    const code = redeemInput.trim()
    if (!code || redeeming) return
    setRedeeming(true)
    setRedeemResult(null)
    try {
      const result = await api.commandCenter.accessCodes.claim({ code })
      setRedeemResult({
        ok: true,
        message: result.message,
        destination: result.target?.destination ?? null,
        targetLabel: result.target?.label ?? null,
        targetType: result.target?.type ?? null,
      })
      setRedeemInput('')
      void fetchCodes()
    } catch (err) {
      setRedeemResult({ ok: false, message: formatError(err) })
    } finally {
      setRedeeming(false)
    }
  }

  function closeIssue() {
    setIssueOpen(false)
    resetIssueForm()
  }

  function openIssue() {
    resetIssueForm()
    setError(null)
    setIssueOpen(true)
  }

  // Drop the plaintext from state on reveal-modal close. This is the
  // ONLY place the plaintext ever lived, and it is gone after this.
  function closeReveal() {
    setRevealCode(null)
    setRevealPrefix(null)
    setCopied(false)
    void fetchCodes()
  }

  const trimmedLabel = label.trim()
  const parsedMaxUses = Number.parseInt(maxUses, 10)
  const maxUsesValid = Number.isFinite(parsedMaxUses) && parsedMaxUses >= 1
  // A chosen target type MUST carry a target id — no code without a destination.
  const targetValid = !targetType || targetId.trim().length > 0
  const canSubmit = trimmedLabel.length > 0 && maxUsesValid && targetValid && !busy

  async function onIssueSubmit() {
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.commandCenter.accessCodes.create({
        label: trimmedLabel,
        audience,
        role,
        max_uses: parsedMaxUses,
        expires_in: expiresIn,
        project_id: projectId.trim() ? projectId.trim() : null,
        ...(targetType
          ? { target_type: targetType, target_id: targetId.trim(), target_label: targetLabel.trim() || undefined }
          : {}),
      })
      // Close the form, then reveal the one-time plaintext exactly once.
      setIssueOpen(false)
      resetIssueForm()
      setRevealPrefix(result.code?.code_prefix ?? null)
      setRevealCode(result.access_code)
      setCopied(false)
    } catch (err) {
      const handled = routeError(err)
      if (handled) closeIssue()
    } finally {
      setBusy(false)
    }
  }

  async function onCopyReveal() {
    if (!revealCode) return
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(revealCode)
        setCopied(true)
      }
    } catch {
      // Clipboard can be blocked by permissions — the plaintext is still
      // visible in the modal for a manual copy. Do not surface an error
      // banner (and never log the code).
      setCopied(false)
    }
  }

  async function onRevokeConfirmed() {
    if (!revokeTarget) return
    setBusy(true)
    setError(null)
    try {
      await api.commandCenter.accessCodes.revoke({ code_id: revokeTarget.id, reason: REVOKE_REASON })
      setRevokeTarget(null)
      await fetchCodes()
    } catch (err) {
      const handled = routeError(err)
      if (handled) setRevokeTarget(null)
    } finally {
      setBusy(false)
    }
  }

  async function onExpireConfirmed() {
    if (!expireTarget) return
    setBusy(true)
    setError(null)
    try {
      await api.commandCenter.accessCodes.expire({ code_id: expireTarget.id })
      setExpireTarget(null)
      await fetchCodes()
    } catch (err) {
      const handled = routeError(err)
      if (handled) setExpireTarget(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <RouteHeader
        kicker="OLIVIUM · SECURE ACCESS"
        title="Secure Access"
        description="Issue, revoke, and expire OLIVIUM access codes. Codes are hash-at-rest — the one-time plaintext is shown exactly once, at issue, and never again."
        crumbs={[
          { label: 'Command Center', href: '/command-center/overview' },
          { label: 'Secure Access' },
        ]}
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => void fetchCodes()} loading={loading}>
              Refresh
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={openIssue}
              disabled={loading || authRequired || forbidden}
            >
              Issue access code
            </Button>
          </>
        }
      />

      {!authRequired && !forbidden && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            marginBottom: 16,
          }}
        >
          {([
            ['Active', summary.active, '#00a67d'],
            ['Claimed', summary.claimed, '#00aad2'],
            ['Revoked', summary.revoked, '#e85d3a'],
            ['Expired', summary.expired, 'rgba(255,255,255,0.35)'],
            ['Total', summary.total, '#7b5ea7'],
          ] as [string, number, string][]).map(([caption, value, accent]) => (
            <div
              key={caption}
              style={{
                flex: '1 1 120px',
                minWidth: 120,
                background: 'rgba(123, 94, 167, 0.04)',
                border: '1px solid rgba(123, 94, 167, 0.12)',
                borderRadius: 12,
                padding: '12px 14px',
              }}
            >
              <div
                style={{
                  fontSize: 9,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'var(--olv-text-tertiary)',
                  fontFamily: 'var(--font-mono)',
                  marginBottom: 6,
                }}
              >
                {caption}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-mono)', color: accent }}>
                {value}
              </div>
            </div>
          ))}
        </div>
      )}

      {!authRequired && !forbidden && (
        <div
          style={{
            marginBottom: 16,
            padding: '14px 16px',
            background: 'rgba(0, 170, 210, 0.04)',
            border: '1px solid rgba(0, 170, 210, 0.14)',
            borderRadius: 12,
          }}
        >
          <div style={{ fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--olv-text-tertiary)', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>
            Redeem &amp; open a bound target
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Input
              value={redeemInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRedeemInput(e.target.value)}
              placeholder="Paste a one-time access code"
              disabled={redeeming}
              style={{ flex: '1 1 260px', fontFamily: 'var(--font-mono)' }}
            />
            <Button variant="primary" size="sm" onClick={() => void onRedeem()} loading={redeeming} disabled={redeeming || !redeemInput.trim()}>
              Redeem
            </Button>
          </div>
          {redeemResult && (
            <div style={{ marginTop: 10 }}>
              {redeemResult.ok ? (
                <div>
                  <div style={{ fontSize: 12.5, color: 'var(--olv-text-secondary)' }}>{redeemResult.message}</div>
                  {redeemResult.destination ? (
                    <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <StatusBadge tone="verified">
                        {redeemResult.targetLabel || redeemResult.targetType || 'target'}
                      </StatusBadge>
                      <a href={redeemResult.destination} style={{ fontSize: 12.5, color: 'var(--olv-cyan, #00aad2)', fontFamily: 'var(--font-mono)' }}>
                        Open destination →
                      </a>
                    </div>
                  ) : (
                    <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--olv-text-tertiary)' }}>
                      This code is not bound to a destination — nothing to open. (Bind a target when issuing to give a code a real destination.)
                    </div>
                  )}
                </div>
              ) : (
                <Banner severity="warning" title="Redeem">{redeemResult.message}</Banner>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 14 }}>
          <Banner severity="warning" title="Secure Access">{error}</Banner>
        </div>
      )}

      {authRequired ? (
        <EmptyState
          title="Sign in to continue"
          body="Secure Access reads live OLIVIUM access codes and requires an authenticated operator session. Sign in at /auth/login to load, issue, revoke, and expire codes."
        />
      ) : forbidden ? (
        <EmptyState
          title="Operator access required"
          body="This is an admin-gated surface. Issuing and controlling OLIVIUM access codes is restricted to the Master Architect and authorized operators. Your account does not have operator access."
        />
      ) : loading ? (
        <InlineAlert severity="info">Loading access codes…</InlineAlert>
      ) : (
        <DataGrid
          columns={[
            {
              id: 'label',
              header: 'LABEL',
              render: (r: CommandCenterAccessCode) => r.label || '—',
            },
            {
              id: 'code_prefix',
              header: 'PREFIX',
              mono: true,
              width: 140,
              render: (r: CommandCenterAccessCode) => (
                <span style={monoCell} title={r.code_prefix}>{r.code_prefix || '—'}</span>
              ),
            },
            {
              id: 'audience',
              header: 'AUDIENCE',
              width: 120,
              render: (r: CommandCenterAccessCode) => r.audience || '—',
            },
            {
              id: 'role',
              header: 'ROLE',
              width: 160,
              render: (r: CommandCenterAccessCode) => r.role || '—',
            },
            {
              id: 'target',
              header: 'TARGET',
              width: 190,
              render: (r: CommandCenterAccessCode) =>
                r.target_type ? (
                  <span style={monoCell} title={`${r.target_type}${r.target_id ? ` · ${r.target_id}` : ''}`}>
                    {r.target_label || r.target_type.replace(/_/g, ' ')}
                  </span>
                ) : (
                  <span style={{ color: 'var(--olv-text-tertiary)' }}>unbound</span>
                ),
            },
            {
              id: 'status',
              header: 'STATUS',
              width: 120,
              render: (r: CommandCenterAccessCode) => (
                <StatusBadge tone={statusTone(r.status)}>{r.status || 'unknown'}</StatusBadge>
              ),
            },
            {
              id: 'uses',
              header: 'USES',
              mono: true,
              width: 90,
              render: (r: CommandCenterAccessCode) => `${r.use_count}/${r.max_uses}`,
            },
            {
              id: 'expires_at',
              header: 'EXPIRES',
              mono: true,
              width: 150,
              render: (r: CommandCenterAccessCode) => formatStamp(r.expires_at),
            },
            {
              id: 'actions',
              header: '',
              width: 150,
              render: (r: CommandCenterAccessCode) => (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => { setExpireTarget(r); setError(null) }}
                  >
                    Expire
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={busy}
                    onClick={() => { setRevokeTarget(r); setError(null) }}
                  >
                    Revoke
                  </Button>
                </div>
              ),
            },
          ]}
          rows={codes}
          getRowId={(r: CommandCenterAccessCode) => r.id}
          emptyMessage="No access codes issued."
        />
      )}

      {/* Issue access code — form modal. */}
      <Modal
        open={issueOpen}
        onClose={busy ? undefined : closeIssue}
        title="Issue access code"
        width={480}
        footer={
          <>
            <Button variant="ghost" onClick={closeIssue} disabled={busy}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => void onIssueSubmit()}
              loading={busy}
              disabled={!canSubmit}
            >
              Issue code
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FormField label="Label" required help="A human-readable name for this code (shown in the list).">
            <Input
              value={label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)}
              placeholder="e.g. KelleyReno owner onboarding"
              disabled={busy}
            />
          </FormField>

          <FormField label="Audience" required>
            <Select
              value={audience}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAudience(e.target.value as Audience)}
              options={AUDIENCE_OPTIONS}
              disabled={busy}
            />
          </FormField>

          <FormField label="Role" required>
            <Select
              value={role}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setRole(e.target.value as Role)}
              options={ROLE_OPTIONS}
              disabled={busy}
            />
          </FormField>

          <FormField
            label="Max uses"
            required
            help="How many times this code may be redeemed. Minimum 1."
            error={maxUses.trim().length > 0 && !maxUsesValid ? 'Enter a whole number of 1 or more.' : undefined}
          >
            <Input
              type="number"
              min={1}
              step={1}
              value={maxUses}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMaxUses(e.target.value)}
              disabled={busy}
              error={maxUses.trim().length > 0 && !maxUsesValid}
            />
          </FormField>

          <FormField label="Expires in" required>
            <Select
              value={expiresIn}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setExpiresIn(e.target.value as ExpiresIn)}
              options={EXPIRES_OPTIONS}
              disabled={busy}
            />
          </FormField>

          <FormField label="Project ID" help="Optional. Scope this code to a single project.">
            <Input
              value={projectId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectId(e.target.value)}
              placeholder="Optional project UUID"
              disabled={busy}
            />
          </FormField>

          <FormField label="Bind to target" help="What this code opens on redeem. A bound code always resolves to a real destination.">
            <Select
              value={targetType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTargetType(e.target.value as TargetType)}
              options={TARGET_OPTIONS}
              disabled={busy}
            />
          </FormField>

          {targetType && (
            <>
              <FormField
                label="Target ID"
                required
                help="The id/ref of the thing this code opens (a workspace/project uuid, an intake invite code, an artifact id)."
                error={!targetValid ? 'A bound code needs a target id — no code without a destination.' : undefined}
              >
                <Input
                  value={targetId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTargetId(e.target.value)}
                  placeholder="e.g. a workspace or project UUID"
                  disabled={busy}
                  error={!targetValid}
                />
              </FormField>
              <FormField label="Target label" help="Optional. A non-secret human label for the destination, shown in lists and on redeem.">
                <Input
                  value={targetLabel}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTargetLabel(e.target.value)}
                  placeholder="e.g. Silver Key partnership brief"
                  disabled={busy}
                />
              </FormField>
            </>
          )}
        </div>
      </Modal>

      {/* One-time reveal — the plaintext is shown exactly once, here. */}
      <Modal
        open={!!revealCode}
        onClose={closeReveal}
        title="Access code issued"
        width={480}
        footer={
          <>
            <Button variant="ghost" onClick={onCopyReveal}>
              {copied ? 'Copied' : 'Copy code'}
            </Button>
            <Button variant="primary" onClick={closeReveal}>Done</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div
            style={{
              background: 'rgba(255,178,36,0.06)',
              border: '1px solid rgba(255,178,36,0.28)',
              borderRadius: 'var(--olv-radius-card)',
              padding: '12px 14px',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--olv-amber)',
                fontFamily: 'var(--font-mono)',
                letterSpacing: '0.06em',
                marginBottom: 4,
              }}
            >
              Copy now — this code will not be shown again
            </div>
            <div style={{ fontSize: 12, color: 'var(--olv-text-secondary)', lineHeight: 1.5 }}>
              The code is stored as a one-way hash. There is no way to recover the plaintext after
              this dialog closes. Copy it now and deliver it over a secure channel.
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: 9,
                color: 'var(--olv-text-tertiary)',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                fontFamily: 'var(--font-mono)',
                marginBottom: 6,
              }}
            >
              Access code{revealPrefix ? ` · prefix ${revealPrefix}` : ''}
            </div>
            <div
              style={{
                background: 'rgba(12,12,20,0.6)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 'var(--olv-radius-card)',
                padding: '12px 14px',
                fontFamily: 'var(--font-mono)',
                fontSize: 15,
                color: 'var(--olv-text-primary)',
                wordBreak: 'break-all',
                userSelect: 'all',
              }}
            >
              {revealCode}
            </div>
          </div>
        </div>
      </Modal>

      {/* Revoke — fixed operator reason; simple confirm. */}
      <ConfirmDialog
        open={!!revokeTarget}
        onCancel={() => setRevokeTarget(null)}
        onConfirm={() => void onRevokeConfirmed()}
        title="Revoke access code"
        message={
          revokeTarget
            ? `Revoke access code "${revokeTarget.label || revokeTarget.code_prefix}"? It immediately stops working and cannot be redeemed. Linked to an Olivium receipt under the operator reason "${REVOKE_REASON}".`
            : ''
        }
        confirmLabel="Revoke"
        variant="danger"
      />

      {/* Expire — simple confirm. */}
      <ConfirmDialog
        open={!!expireTarget}
        onCancel={() => setExpireTarget(null)}
        onConfirm={() => void onExpireConfirmed()}
        title="Expire access code"
        message={
          expireTarget
            ? `Expire access code "${expireTarget.label || expireTarget.code_prefix}"? It will be marked expired and can no longer be redeemed. Linked to an Olivium receipt.`
            : ''
        }
        confirmLabel="Expire"
        variant="primary"
      />
    </div>
  )
}
